import React, { useEffect, useState } from 'react';
import MainLayoutWithoutFooter from '../components/MainLayoutWithoutFooter';
import { callAPI } from '../utils/apiUtils';
import { apiUrls } from '../utils/apiUrls';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import {
  DragDropContext,
  Droppable,
  Draggable
} from '@hello-pangea/dnd';

const OrderTransferOrder = () => {
  const [records, setRecords] = useState([]);
  const [fromTable, setFromTable] = useState('');
  const [toTable, setToTable] = useState('');
  const [fromOrders, setFromOrders] = useState([]);
  const [rightOrders, setRightOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTableListing();
  }, []);

  useEffect(() => {
    if (fromTable) {
      fetchOrdersByTable(fromTable);
      setRightOrders([]);
    } else {
      setFromOrders([]);
      setRightOrders([]);
    }
  }, [fromTable]);

  const fetchTableListing = async () => {
    try {
      const resp = await callAPI(apiUrls.TABLE_LISTING, { }, 'GET');
      if (resp.data?.tables) {
        setRecords(resp.data.tables);
      }
    } catch (err) {
      toast.error('Failed to load tables');
      console.error(err);
    }
  };

  const fetchOrdersByTable = async (tableId) => {
    try {
      const resp = await callAPI(`${apiUrls.ORDER_BY_TABLE_ID}?table_id=${tableId}`, { }, 'GET');
      
      if (resp.data?.data) {
        setFromOrders(resp.data.data);
      }
    } catch (err) {
      toast.error('Failed to fetch orders for selected table');
      console.error(err);
    }
  };

  const handleDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    
    if (
      source.droppableId === 'fromOrders' &&
      destination.droppableId === 'rightOrders'
    ) {
      const moved = fromOrders[source.index];
      const newFrom = Array.from(fromOrders);
      newFrom.splice(source.index, 1);
      const newRight = Array.from(rightOrders);
      newRight.splice(destination.index, 0, moved);
      setFromOrders(newFrom);
      setRightOrders(newRight);
    }
    
    else if (
      source.droppableId === 'rightOrders' &&
      destination.droppableId === 'fromOrders'
    ) {
      const moved = rightOrders[source.index];
      const newRight = Array.from(rightOrders);
      newRight.splice(source.index, 1);
      const newFrom = Array.from(fromOrders);
      newFrom.splice(destination.index, 0, moved);
      setRightOrders(newRight);
      setFromOrders(newFrom);
    }
    
    else if (source.droppableId === destination.droppableId) {
      const list = source.droppableId === 'fromOrders'
        ? Array.from(fromOrders)
        : Array.from(rightOrders);
      const [removed] = list.splice(source.index, 1);
      list.splice(destination.index, 0, removed);
      if (source.droppableId === 'fromOrders') {
        setFromOrders(list);
      } else {
        setRightOrders(list);
      }
    }
  };

  const handleSubmit = async () => {
    if (!fromTable) {
      return toast.error('Please select occupied table.');
    }
    if (!toTable) {
      return toast.error('Please select target table.');
    }
    if (rightOrders.length < 1) {
      return toast.error('Transfer items to complete action.');
    }

    const toOrderIds = rightOrders.map((ord) => ord.id);

    const payload = {
      from_table: fromTable,
      to_table: toTable,
      to_order_ids: JSON.stringify(toOrderIds),
    };

    try {
      setLoading(true);
      const resp = await callAPI(apiUrls.UPDATE_TABLE_ORDERS, {}, 'POST', payload, {});
      if (resp.status) {
        toast.success(resp.data.message || 'Orders transferred successfully!');
        
        setFromTable('');
        setToTable('');
        setFromOrders([]);
        setRightOrders([]);
        fetchTableListing();
      } else {
        toast.error(resp.data.message || 'Failed to transfer orders.');
      }
    } catch (err) {
      toast.error('Error during transfer.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayoutWithoutFooter>
      <div className="pageContent transferOptionsPage dflexCol pb-3">
        <div className="container">
          <div className="selectCustomerCol">
            <h4 className="lgTitle text-center pb-3">Select a Table</h4>
            <div className="formStyle">
              <div className="row g-3 g-lg-4">
                <div className="col-12 col-md-6">
                  <label className="form-label">Transfer Order From Table</label>
                  <select
                    className="form-select"
                    value={fromTable}
                    onChange={(e) => setFromTable(e.target.value)}
                  >
                    <option value="">Select Occupied Table</option>
                    {records
                      .filter((t) => t.table_id)
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">Transfer Order To Table</label>
                  <select
                    className="form-select"
                    value={toTable}
                    onChange={(e) => setToTable(e.target.value)}
                  >
                    <option value="">Select Available or Occupied Table</option>
                    {records.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} {t.table_id ? '(Occupied)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 mt-4">
                  {fromOrders.length > 0 && (
                    <p className="text-muted text-center">
                      Drag/order items from left to right to transfer.
                    </p>
                  )}
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <div className="row">
                      <div className="col-md-6">
                        <Droppable droppableId="fromOrders">
                          {(provided) => (
                            <div
                              className="list-group"
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                            >
                              {fromOrders.map((order, idx) => (
                                <Draggable
                                  key={order.id}
                                  draggableId={String(order.id)}
                                  index={idx}
                                >
                                  {(prov) => (
                                    <div
                                      className="list-group-item mb-2 orderBorder"
                                      ref={prov.innerRef}
                                      {...prov.draggableProps}
                                      {...prov.dragHandleProps}
                                    >
                                      <p className="orderHeader font-weight-bold">
                                        {order.orderid}{' '}
                                        {order.parent_order ? '- Main Order' : ''}
                                      </p>
                                      <ul>
                                        {order.items.map((item, i2) => (
                                          <li
                                            key={i2}
                                            className="d-flex justify-content-between list-group-items"
                                            style={item.is_deleted ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}
                                          >
                                            <span>{item.name}{item.is_deleted ? ' (Deleted)' : ''}</span>
                                            <strong>
                                              {item.qty} x {item.per_item_price}
                                            </strong>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </div>
                      <div className="col-md-6">
                        <Droppable droppableId="rightOrders">
                          {(provided) => (
                            <div
                              className="list-group"
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                            >
                              {rightOrders.map((order, idx) => (
                                <Draggable
                                  key={order.id}
                                  draggableId={`right-${order.id}`}
                                  index={idx}
                                >
                                  {(prov) => (
                                    <div
                                      className="list-group-item mb-2 orderBorder"
                                      ref={prov.innerRef}
                                      {...prov.draggableProps}
                                      {...prov.dragHandleProps}
                                    >
                                      <p className="orderHeader font-weight-bold">
                                        {order.orderid}{' '}
                                        {order.parent_order ? '- Main Order' : ''}
                                      </p>
                                      <ul>
                                        {order.items.map((item, i2) => (
                                          <li
                                            key={i2}
                                            className="d-flex justify-content-between list-group-items"
                                            style={item.is_deleted ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}
                                          >
                                            <span>{item.name}{item.is_deleted ? ' (Deleted)' : ''}</span>
                                            <strong>
                                              {item.qty} x {item.per_item_price}
                                            </strong>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </div>
                    </div>
                  </DragDropContext>
                </div>

                <div className="col-12 text-center mt-4">
                  <button
                    type="button"
                    className="btn btnPrimary"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading ? 'Transferring...' : 'Transfer Orders'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayoutWithoutFooter>
  );
};

export default OrderTransferOrder;
